import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronRight,
  Images,
  ArrowLeft,
  Search,
  X,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ImageIcon,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const { language, t } = useLanguage();
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  const isRu = language === 'ru';

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const fetchedAlbums = response.data.albums || [];

      const transformedAlbums = fetchedAlbums.map((album, index) => ({
        id: index + 1,
        title: isRu && album.titleRu ? album.titleRu : album.title,
        cover: album.coverImage,
        images: album.galleryImages || [],
      }));

      setAlbums(transformedAlbums);
    } catch (error) {
      console.error('Error fetching albums:', error);
      setAlbums([]);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const showPrevImage = () => {
    if (!selectedAlbum) return;
    setLightboxIndex((prev) =>
      prev === 0 ? selectedAlbum.images.length - 1 : prev - 1
    );
  };

  const showNextImage = () => {
    if (!selectedAlbum) return;
    setLightboxIndex((prev) =>
      prev === selectedAlbum.images.length - 1 ? 0 : prev + 1
    );
  };

  const content = {
    home: isRu ? 'Главная' : 'Acasă',
    page: isRu ? 'Servicii' : 'Servicii',
    title: isRu ? 'Galerie și servicii AgroSmart' : 'Galerie și servicii AgroSmart',
    desc: isRu
      ? 'Descoperă lucrările, produsele și proiectele prezentate de AgroSmart într-o galerie simplă și organizată.'
      : 'Descoperă lucrările, produsele și proiectele prezentate de AgroSmart într-o galerie simplă și organizată.',
    albumsTitle: isRu ? 'Albume disponibile' : 'Albume disponibile',
    albumsDesc: isRu
      ? 'Alege un album pentru a vedea imaginile și detaliile.'
      : 'Alege un album pentru a vedea imaginile și detaliile.',
    empty: isRu ? 'Nu există albume disponibile.' : 'Nu există albume disponibile.',
    imagesCount: isRu ? 'imagini' : 'imagini',
    albumImages: isRu ? 'imagini în album' : 'imagini în album',
    back: isRu ? 'Înapoi la albume' : 'Înapoi la albume',
    openAlbum: isRu ? 'Deschide albumul' : 'Deschide albumul',
    lightboxImage: isRu ? 'Imaginea' : 'Imaginea',
    lightboxFrom: isRu ? 'din' : 'din',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* BREADCRUMB */}
      <div className="bg-white border-b">
        <div className="w-full px-3 md:px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 overflow-x-auto whitespace-nowrap">
            <Link to="/" className="hover:text-[#a7cf26]">
              {content.home}
            </Link>

            <ChevronRight className="w-4 h-4 text-gray-400" />

            {!selectedAlbum ? (
              <span className="text-gray-900 font-semibold">
                {content.page}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAlbum(null);
                    setLightboxIndex(null);
                  }}
                  className="hover:text-[#a7cf26] font-semibold"
                >
                  {content.page}
                </button>

                <ChevronRight className="w-4 h-4 text-gray-400" />

                <span className="text-gray-900 font-semibold">
                  {selectedAlbum.title}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="w-full px-3 md:px-6 py-8 md:py-10">
        {loading ? (
          <div className="min-h-[360px] flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a7cf26] mx-auto mb-4"></div>
              <p className="text-gray-500 text-sm">
                {isRu ? 'Загрузка...' : 'Se încarcă...'}
              </p>
            </div>
          </div>
        ) : albums.length === 0 ? (
          <div className="bg-white rounded-[28px] border border-gray-100 p-10 md:p-14 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#a7cf26]/10">
              <Images className="w-8 h-8 text-[#a7cf26]" />
            </div>

            <p className="text-gray-700 font-bold">{content.empty}</p>
          </div>
        ) : !selectedAlbum ? (
          <>
            <section className="mb-6 md:mb-8 bg-white border border-gray-100 rounded-[28px] p-5 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div>
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#a7cf26]/10">
                    <Images className="w-7 h-7 text-[#a7cf26]" />
                  </div>

                  <h1 className="mt-5 text-3xl md:text-5xl font-extrabold leading-tight text-gray-950">
                    {content.title}
                  </h1>

                  <p className="mt-4 max-w-2xl text-sm md:text-base leading-7 text-gray-500">
                    {content.desc}
                  </p>
                </div>

                <div className="rounded-[24px] bg-gray-950 px-6 py-5 text-white w-fit">
                  <div className="text-3xl font-black text-[#a7cf26]">
                    {albums.length}
                  </div>
                  <div className="text-xs font-semibold text-white/70">
                    {content.albumsTitle}
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="mb-6 bg-white border border-gray-100 rounded-[28px] p-5 md:p-7">
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-950">
                  {content.albumsTitle}
                </h2>

                <p className="mt-2 text-sm md:text-base text-gray-500">
                  {content.albumsDesc}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
                {albums.map((album) => (
                  <button
                    key={album.id}
                    onClick={() => {
                      setSelectedAlbum(album);
                      setLightboxIndex(null);
                    }}
                    className="group bg-white rounded-[26px] overflow-hidden text-left border border-gray-100"
                  >
                    <div className="relative h-[260px] md:h-[300px] overflow-hidden bg-gray-100">
                      <img
                        src={album.cover}
                        alt={album.title}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-300"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />

                      <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-extrabold text-gray-900">
                        <ImageIcon className="w-4 h-4 text-[#a7cf26]" />
                        {album.images.length} {content.imagesCount}
                      </div>

                      <div className="absolute left-4 right-4 bottom-4">
                        <h3 className="text-white text-xl md:text-2xl font-extrabold leading-tight line-clamp-2">
                          {album.title}
                        </h3>
                      </div>
                    </div>

                    <div className="p-4 flex items-center justify-between gap-3">
                      <span className="text-sm font-bold text-gray-700">
                        {content.openAlbum}
                      </span>

                      <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#a7cf26]/10">
                        <ChevronRight className="w-5 h-5 text-[#a7cf26]" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="mb-6 bg-white border border-gray-100 rounded-[28px] p-5 md:p-7">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-4xl font-extrabold text-gray-950">
                    {selectedAlbum.title}
                  </h1>

                  <p className="mt-2 text-sm md:text-base text-gray-500">
                    {selectedAlbum.images.length} {content.albumImages}
                  </p>
                </div>

                <button
                  onClick={() => {
                    setSelectedAlbum(null);
                    setLightboxIndex(null);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[#a7cf26] px-5 py-3 text-sm font-extrabold text-[#a7cf26] hover:bg-[#a7cf26]/10 w-fit"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {content.back}
                </button>
              </div>
            </section>

            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 md:gap-5 [column-fill:_balance]">
              {selectedAlbum.images.map((image, index) => (
                <div key={index} className="mb-4 md:mb-5 break-inside-avoid">
                  <button
                    type="button"
                    onClick={() => openLightbox(index)}
                    className="group relative block w-full overflow-hidden rounded-[24px] border border-gray-100 bg-white"
                  >
                    <img
                      src={image}
                      alt={`${selectedAlbum.title} ${index + 1}`}
                      className="w-full h-auto"
                    />

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 scale-95 group-hover:scale-100 transition duration-300">
                        <Search className="w-6 h-6 text-[#a7cf26]" />
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* LIGHTBOX */}
      {selectedAlbum && lightboxIndex !== null && (
        <div className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center p-3 md:p-4">
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-5 md:right-5 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            type="button"
            onClick={showPrevImage}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <div className="max-w-6xl w-full flex flex-col items-center">
            <img
              src={selectedAlbum.images[lightboxIndex]}
              alt={`${selectedAlbum.title} ${lightboxIndex + 1}`}
              className="max-h-[82vh] w-auto max-w-full object-contain rounded-[18px]"
            />

            <div className="mt-4 text-center text-white">
              <div className="font-semibold text-base md:text-lg">
                {selectedAlbum.title}
              </div>

              <div className="text-sm text-white/75">
                {content.lightboxImage} {lightboxIndex + 1}{' '}
                {content.lightboxFrom} {selectedAlbum.images.length}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={showNextImage}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <ChevronRightIcon className="w-7 h-7" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;